import { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";
import { message } from "antd";
import User from "../../redux/models/user";
import { v2DefaultColDef } from "./options";
import AgGridUtility from "../../utilities/agGridUtility";

export const useDataGrid = (userId, key, defaultColumnConfig, useGlobalLoading = true) => {
  const [columnDefs, setColumnDefs] = useState(null);

  const saveColumnConfig = useCallback(
    (config) => {
      return User.saveColumnSort({
        usersId: userId,
        configJson: JSON.stringify(config),
        tableName: key,
      });
    },
    [userId, key],
  );

  const getColumnConfig = useCallback(() => {
    return User.getTableConfiguration(key, useGlobalLoading);
  }, [key, useGlobalLoading]);

  const onColumnsUpdated = useCallback(
    debounce(async ({ columnApi }) => {
      try {
        const data = AgGridUtility.prepareColumnStateToSave(
          defaultColumnConfig,
          columnApi.getColumnState(),
        );

        await saveColumnConfig(data);
        message.success("Table configuration saved successfully");
      } catch (err) {
        console.error(err);
      }
    }, 1000),
    [saveColumnConfig, defaultColumnConfig],
  );

  const getRowId = useCallback((params) => {
    return `${params.data.id}`;
  }, []);

  const onGridReady = useCallback(async () => {
    try {
      const res = await getColumnConfig();

      if (res) {
        const config = JSON.parse(res.config);
        const data = AgGridUtility.mergeSavedColumnState(
          defaultColumnConfig,
          config,
        );
        setColumnDefs(data);
      } else {
        setColumnDefs(defaultColumnConfig);
      }
    } catch (err) {
      console.error(err);
    }
  }, [getColumnConfig, setColumnDefs, defaultColumnConfig]);

  useEffect(() => {
    return () => onColumnsUpdated.cancel();
  }, []);

  return {
    columnDefs,
    defaultColDef: v2DefaultColDef,
    getRowId,
    onGridReady,
    onColumnMoved: onColumnsUpdated,
    onColumnPinned: onColumnsUpdated,
    onColumnVisible: onColumnsUpdated,
    onSortChanged: onColumnsUpdated,
    cacheBlockSize: 25,
  };
};
